import { Experiment } from '@amplitude/experiment-node-server';
import { FeatureFlagVariant } from '@modules/feature-flag';
import { getCookie } from 'cookies-next';
import { NEXT_PUBLIC_AMPLITUDE_EXPERIMENTS_KEY } from '@env';

import { IncomingMessage } from 'http';
import { NextApiRequestCookies } from 'next/dist/server/api-utils';

const experimentClient = Experiment.initializeRemote(NEXT_PUBLIC_AMPLITUDE_EXPERIMENTS_KEY, {
  fetchTimeoutMillis: 500,
  fetchRetries: 1,
  fetchRetryBackoffMinMillis: 0,
  fetchRetryTimeoutMillis: 600,
});

/**
 * Get the variant of a feature flag for a user directly from the Amplitude Experiment API.
 * This is useful for server-side rendering, where the client-side experiment client is not available.
 * Or when you aren't able to use the hook and rely on the client SDK to be ready before you need the variant client-side.
 */
export const getFeatureVariant = async <T extends keyof FeatureFlagVariant>(
  experimentFlag: T,
  fallback: FeatureFlagVariant[T],
  {
    req,
    userId,
    userProperties,
  }: {
    req?: IncomingMessage & {
      cookies: NextApiRequestCookies;
    };
    userId?: string;
    userProperties?: {
      [propertyName: string]: string | number | boolean | (string | number | boolean)[];
    };
  }
): Promise<FeatureFlagVariant[T]> => {
  const variants = await experimentClient.fetchV2({
    user_id: userId,
    user_properties: userProperties,
    device_id: getCookie('blinq_viewer_id', { req }),
  });

  return (variants[experimentFlag]?.value as FeatureFlagVariant[T]) || fallback;
};
